import React from "react";
import { FileText, ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  isImageDocument,
  getFileExtensionFromUrl,
  getDocumentTypeName,
} from "@/hooks/usePatientDocuments";
import type { DocumentType } from "@/lib/api";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface DocumentFileCardProps {
  /** Unique document ID */
  id: string;
  /** File URL for thumbnail / type detection */
  fileUrl: string;
  /** Display name (description or filename) */
  name: string;
  /** Document type code (e.g. "LR", "SC", "PR") */
  documentType?: DocumentType | null;
  /** Compact mode shows smaller cards (used in inline card grids) */
  compact?: boolean;
  /** Click handler */
  onClick?: () => void;
  /** Additional CSS classes */
  className?: string;
}

/** Max characters for the display name before truncation */
const MAX_NAME_LENGTH_COMPACT = 10;
const MAX_NAME_LENGTH_NORMAL = 14;

/** Extract a readable filename from a URL (strips query params, path, and extension) */
const getFileNameFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const rawName = pathname.split("/").pop() || "";
    // Remove extension and decode URI
    const decoded = decodeURIComponent(rawName);
    return decoded || "";
  } catch {
    const raw = url.split("?")[0].split("/").pop() || "";
    return decodeURIComponent(raw);
  }
};

/**
 * Shared document file card used across Conversation Uploads, Medicines,
 * Reports & Imaging tabs. Shows a thumbnail (image preview or file-type icon),
 * truncated file name, and document type label.
 */
export const DocumentFileCard = ({
  id,
  fileUrl,
  name,
  documentType,
  compact = false,
  onClick,
  className,
}: DocumentFileCardProps) => {
  const isImage = isImageDocument(fileUrl);
  const fileExtension = getFileExtensionFromUrl(fileUrl) || "file";
  // Use description if available, otherwise extract filename from URL
  const resolvedName =
    name && name.trim() ? name : getFileNameFromUrl(fileUrl) || "Document";
  const maxLen = compact ? MAX_NAME_LENGTH_COMPACT : MAX_NAME_LENGTH_NORMAL;
  const displayName =
    resolvedName.length > maxLen
      ? `${resolvedName.substring(0, maxLen)}...`
      : resolvedName;
  const typeName = documentType ? getDocumentTypeName(documentType) : null;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            key={id}
            className={cn(
              "group relative rounded-xl flex flex-col items-center transition-all cursor-pointer overflow-hidden",
              "bg-background border border-border/60",
              "hover:shadow-md hover:scale-[1.02] hover:border-primary/40",
              compact ? "p-1.5 gap-0.5" : "p-2 gap-1",
              className
            )}
            onClick={onClick}
          >
            {/* Thumbnail */}
            <div
              className={cn(
                "rounded-lg overflow-hidden flex items-center justify-center",
                "bg-muted/60 border border-border/60",
                compact ? "w-10 h-12" : "w-14 h-16"
              )}
            >
              {isImage ? (
                <>
                  <img
                    src={fileUrl}
                    alt={name}
                    className="w-full h-full object-cover rounded-lg"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      const fallback = target.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = "flex";
                    }}
                  />
                  {/* Fallback if image fails */}
                  <div
                    className="hidden flex-col items-center justify-center text-center"
                    style={{ display: "none" }}
                  >
                    <ImageIcon
                      className={cn(
                        "text-primary mx-auto mb-0.5",
                        compact ? "h-4 w-4" : "h-5 w-5"
                      )}
                    />
                    <span className="z-hint text-muted-foreground uppercase">
                      {fileExtension}
                    </span>
                  </div>
                </>
              ) : (
                <div className="text-center flex flex-col items-center justify-center">
                  <FileText
                    className={cn(
                      "text-destructive mx-auto mb-0.5",
                      compact ? "h-4 w-4" : "h-5 w-5"
                    )}
                  />
                  <span className="z-hint text-muted-foreground uppercase">
                    {fileExtension.toUpperCase()}
                  </span>
                </div>
              )}
            </div>

            {/* File name */}
            <span
              className={cn(
                "text-foreground text-center w-full px-0.5 truncate",
                compact ? "z-hint" : "z-caption"
              )}
            >
              {displayName}
            </span>

            {/* Document type label */}
            {typeName && (
              <span
                className={cn(
                  "text-muted-foreground text-center w-full truncate",
                  compact ? "z-hint" : "z-caption"
                )}
              >
                {typeName}
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[200px]">
          <p className="z-caption break-words">{resolvedName}</p>
          {typeName && (
            <p className="z-hint text-muted-foreground mt-0.5">{typeName}</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
